/** @format */

import { Chain } from '@shared/data-access/interfaces';
import { GamData, EvergreenRedirectResponse } from './google-ad-manager.interface';
import {
	Component,
	Input,
	OnInit,
	Inject,
	HostListener,
	input,
	signal,
	computed,
	PLATFORM_ID
} from '@angular/core';

import { AnalyticsService, Logger, UtilityService, WINDOW } from '@shared/data-access/services';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
	homefieldIconAlertInfoOutlined,
	HomefieldIconLibraryModule,
	homefieldIconSignCircle,
	HomefieldIconsLibraryService
} from '@dsg-tech/homefield-angular-icons';
import { HttpClient } from '@angular/common/http';

@Component({
	selector: 'shared-components-google-ad-manager',
	templateUrl: './google-ad-manager.component.html',
	styleUrl: './google-ad-manager.component.scss',
	imports: [CommonModule, HomefieldIconLibraryModule],
	standalone: true
})
export class GoogleAdManagerComponent implements OnInit {
	@Input() data: string;
	@Input() breadcrumbData = null;
	@Input() addlData: any = null;
	showSponsoredLabel = input<boolean>(false);
	readonly hasTopMargin = input(true);
	enableTooltipSurvey = input(true);

	gamData: GamData | undefined;
	isDsgStorePage: boolean;
	readonly hideAdLabel = signal<boolean>(true);
	readonly showSponsoredLabelUpdated = signal<boolean>(false);
	readonly shouldShowSponsoredLabel = computed(
		() => this.showSponsoredLabelUpdated() && !this.hideAdLabel()
	);

	/* Ad Carousel variables */
	readonly enableAdCarousel = input(false);
	readonly isNarrowBanner = input<boolean>(false);
	currentSlide = signal<number>(0);
	carouselBaseUrl = 'https://images.dickssportinggoods.com/medianetwork/';
	evergreenRedirectUrl: string;
	placeholderHeight: string;
	evergreenCampaign: number;
	imageName: string;

	constructor(
		private readonly analyticsService: AnalyticsService,
		@Inject(WINDOW) protected window: Window,
		private readonly chain: Chain,
		homefieldIconsLibraryService: HomefieldIconsLibraryService,
		private readonly utilityService: UtilityService,
		private readonly http: HttpClient,
		private readonly logger: Logger,
		@Inject(PLATFORM_ID) private readonly platformId: string
	) {
		homefieldIconsLibraryService.registerIcons([
			homefieldIconAlertInfoOutlined,
			homefieldIconSignCircle
		]);
	}

	ngOnInit(): void {
		this.showSponsoredLabelUpdated.set(
			this.addlData?.data[0]?.showSponsoredLabel ?? this.showSponsoredLabel()
		);
		if (this.enableAdCarousel() && isPlatformBrowser(this.platformId)) {
			this.evergreenCampaign = Math.floor(Math.random() * 5);
			this.setStyleProperties();
			this.getEvergreenJson();
		}

		this.utilityService.deferOnPrerendering(() => this.triggerAnalytics());

		this.isDsgStorePage =
			this.window.location?.href.endsWith('/s/stores') &&
			this.chain.chainIdentifierAbbr === 'dsg';
	}

	private triggerAnalytics(): void {
		const analyticsEventObject = { AdSpotIDs: this.data };
		// add breadcrumbs data to analytics event if category page has breadcrumbs
		if (this.breadcrumbData) {
			analyticsEventObject['Breadcrumbs'] = this.breadcrumbData;
		}

		this.analyticsService.triggerAnalyticsEvent('AdSpotsReady', analyticsEventObject);
	}

	private checkAdVisibility(): void {
		const banner = this.window.document.getElementById(this.data);
		const iframe = banner?.querySelector<HTMLIFrameElement>('iframe');
		if (!iframe) {
			this.hideAdLabel.set(true);
			return;
		}

		const widthAttr = parseInt(iframe.getAttribute('width') || '0');
		const heightAttr = parseInt(iframe.getAttribute('height') || '0');

		if (widthAttr > 0 || heightAttr > 0) {
			this.hideAdLabel.set(false);
			if (this.enableAdCarousel()) {
				this.currentSlide.set(1);
			}
		}
	}

	loadSurvey(): void {
		if (typeof this.window.KAMPYLE_ONSITE_SDK !== 'undefined' && this.enableTooltipSurvey()) {
			this.window.gamData = this.gamData;
			if (this.chain.chainIdentifierAbbr === 'dsg') {
				this.window.KAMPYLE_ONSITE_SDK.showForm(15821);
			} else if (this.chain.chainIdentifierAbbr === 'gg') {
				this.window.KAMPYLE_ONSITE_SDK.showForm(16385);
			}
		}
	}

	@HostListener('window:message', ['$event'])
	onMessage(event: MessageEvent): void {
		if (!this.enableTooltipSurvey() && !this.enableAdCarousel()) {
			return;
		}
		const allowedHosts = ['safeframe.googlesyndication.com'];
		let originHost: string;
		try {
			originHost = new URL(event.origin).hostname;
		} catch (e) {
			return;
		}

		if (originHost.endsWith(allowedHosts[0])) {
			const adData = event.data;
			let parsedAdData;
			// Google pumps out a lot of events with this hostname, sometimes the data is not json
			try {
				parsedAdData = JSON.parse(adData);
			} catch (e) {
				return;
			}

			if (parsedAdData?.Header && !this.gamData) {
				this.gamData = parsedAdData;
			}

			this.checkAdVisibility();
		}
	}

	setStyleProperties(): void {
		let evergreenSlideHeight: string;
		if (this.isNarrowBanner()) {
			this.imageName = 'narrow';
			evergreenSlideHeight = '100px';
			this.setBackgroundImages(this.imageName, [
				'1512',
				'1440',
				'1280',
				'925',
				'834',
				'767',
				'567',
				'475',
				'390',
				'375'
			]);
		} else {
			this.imageName = 'ribbon';
			evergreenSlideHeight = '39px';
			this.setBackgroundImages(this.imageName, [
				'1512',
				'1440',
				'1280',
				'1024',
				'834',
				'744',
				'656',
				'567',
				'475',
				'390',
				'375'
			]);
		}

		document.documentElement.style.setProperty('--evergreen-slide-ht', evergreenSlideHeight);
	}

	private setBackgroundImages(imageName: string, imageSizes: string[]): void {
		imageSizes.forEach((size) => {
			document.documentElement.style.setProperty(
				`--evergreen-bg-image-${size}`,
				`url('${this.carouselBaseUrl}${imageName}_${this.evergreenCampaign}_${size}.png')`
			);
		});
	}

	private getEvergreenJson(): void {
		this.http
			.get(`${this.carouselBaseUrl}${this.imageName}_redirect_${this.evergreenCampaign}.json`)
			.subscribe({
				next: (response: EvergreenRedirectResponse) => {
					this.evergreenRedirectUrl = response.redirect;
				},
				error: (error) => {
					this.logger.error('Error loading Evergreen JSON:', error);
				}
			});
	}

	onEvergreenSlideClick(): void {
		if (this.evergreenRedirectUrl) {
			this.window.open(this.evergreenRedirectUrl, '_blank');
		}
	}
}
